/**
 * Defines the VerticalMenuItem component, which renders a single item in the vertical menu.
 *
 * @module VerticalMenuItem
 */
import React, { useState } from 'react';
import { MenuItem, ListItemIcon, Tooltip, ListItemText } from '@mui/material';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

/**
 * CustomTooltip provides a stylised Tooltip component with a specific appearance,
 * including custom colors, rounded corners, and font sizing.
 */
const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    '& .MuiTooltip-tooltip': {
        backgroundColor: '#4B4F54',
        color: '#FFFFFF',
        borderRadius: 20, // Makes it oval
        fontSize: '0.875rem', // Matches MUI's Typography body2 font size
        paddingLeft: 17,
        paddingRight: 17
    }
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    justifyContent: 'center', // Centers the menu item content,
    flexDirection: 'column',
    width: 56,
    height: 56,
    paddingTop: 10,
    borderRadius: 8
}));

/**
 * Renders a menu item for the desktop vertical menu bar, including icon and title.
 * The component expects a configuration object for the menu item.
 * The item either opens a dialog component as specified in its config, or is given onClick functionality instead.
 *
 * @component
 * @param {Object} itemConfig - Configuration object for the menu item, including title, IconComponent, and optional DialogComponent
 * @param {Boolean} disabled - Indicates whether the menu item is disabled
 * @param {Boolean} isDialog - true if the item opens a dialog component
 * @param {Function} onClick - onClick functionality for the item, ignored if the item opens a dialog
 * @param {Boolean} selectedItem - Currently selected (highlighted) vertical menu item
 * @param {Function} setSelectedItem - Function to set the currently selected item
 * @param {Function} resetSelectedMenuItem - Function to reset the currently selected menu item back to inital
 * @returns {React.Element}
 */
export const VerticalMenuItem = ({
    itemConfig,
    disabled = false,
    isDialog = false,
    onClick,
    selectedItem,
    setSelectedItem,
    resetSelectedMenuItem
}) => {
    const theme = useTheme();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleMenuItemClick = () => {
        setSelectedItem(itemConfig.id);
        isDialog ? setIsDialogOpen(true) : onClick();
    };

    const handleCloseDialog = () => {
        resetSelectedMenuItem();
        setIsDialogOpen(false);
    };

    return (
        <>
            <CustomTooltip title={itemConfig.title} placement='right'>
                <StyledMenuItem
                    onClick={handleMenuItemClick}
                    disabled={disabled}
                    disableRipple
                    data-analyticsid={itemConfig.dataAnalyticsId}
                >
                    <ListItemIcon
                        sx={{ minWidth: 'auto', justifyContent: 'center' }}
                    >
                        {selectedItem === itemConfig.id ? (
                            <itemConfig.IconComponent.filled
                                sx={{ fontSize: '2rem' }}
                            />
                        ) : (
                            <itemConfig.IconComponent.outline
                                sx={{ fontSize: '2rem' }}
                            />
                        )}
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{
                            fontSize: '0.72rem',
                            color: theme.palette.grey.dark,
                            fontWeight: 500
                        }}
                    >
                        {itemConfig.title.split(' ')[0]}
                    </ListItemText>
                </StyledMenuItem>
            </CustomTooltip>
            {isDialog && (
                <itemConfig.DialogComponent
                    open={isDialogOpen}
                    handleFormClose={handleCloseDialog}
                    handleSubmissionViewClose={handleCloseDialog}
                />
            )}
        </>
    );
};

VerticalMenuItem.propTypes = {
    itemConfig: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    isDialog: PropTypes.bool,
    onClick: PropTypes.func,
    selectedItem: PropTypes.string,
    setSelectedItem: PropTypes.func,
    resetSelectedMenuItem: PropTypes.func
};
